(() => {
  const interactive = () => !window.Shopify?.designMode ||
    (window.Shopify.inspectMode !== true && document.documentElement.getAttribute('data-raith-editor-inspector') !== 'active');
  const variantId = value => /^[1-9]\d*$/.test(String(value || ''));
  const receiptHas = (event, id) => Array.isArray(event.detail?.items)
    && event.detail.items.some(item => String(item?.variant_id || item?.id) === String(id));

  class RaithLookItem extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('product-form form[action*="/cart/add"]');
      if (!this.form) return;
      this.form.addEventListener('submit', event => this.beforeSubmit(event), {capture: true, signal});
      for (const type of ['input', 'change']) this.addEventListener(type, event => {
        if (event.target !== this.field()) return;
        const lockedId = this.pending?.id || this.uncertainId;
        if (lockedId) {
          this.field().value = lockedId;
          event.stopImmediatePropagation();
          return;
        }
        this.closest('raith-shop-look')?.markInteraction();
        this.message('[data-look-status]', '');
        this.message('[data-look-error]', '');
        this.sync();
      }, {capture: true, signal});
      this.form.addEventListener('formdata', event => {
        if (!this.pending || !event.formData) return;
        // Snapshot the explicit choice, even if another listener yielded before reading the form.
        this.field().value = this.pending.id;
        event.formData.set('id', this.pending.id);
      }, {signal});
      this.form.addEventListener('variant:add', event => this.added(event), {signal});
      this.form.addEventListener('cart:error', event => {
        if (event.target !== this.form || !this.pending) return;
        this.pending = null;
        this.message('[data-look-error]', event.detail?.error || this.dataset.errorLabel || 'Unable to add this item. Please try again.');
        this.sync();
      }, {signal});
      this.form.addEventListener('cart:uncertain', event => {
        if (event.target !== this.form || !this.pending) return;
        this.unconfirmed();
      }, {signal});
      this.sync();
    }
    disconnectedCallback() { this.controller?.abort(); }
    field() { return this.form?.elements.namedItem('id'); }
    choice() {
      const field = this.field();
      if (!field || !variantId(field.value)) return null;
      if (field.tagName === 'SELECT') {
        const option = field.selectedOptions[0];
        if (!option || option.disabled) return null;
      } else if (field.dataset.available !== 'true') return null;
      return field.value;
    }
    message(selector, value) {
      const element = this.querySelector(selector);
      if (element) { element.textContent = value; element.hidden = !value; }
    }
    unconfirmed() {
      this.uncertainId = this.pending?.id || this.uncertainId;
      this.pending = null;
      this.uncertain = true;
      this.message('[data-look-error]', this.dataset.uncertainLabel || 'Please check your bag before trying again.');
      const recovery = this.querySelector('[data-look-recovery]');
      if (recovery) recovery.hidden = false;
      this.sync();
    }
    sync() {
      const field = this.field();
      const locked = Boolean(this.pending || this.uncertain);
      this.toggleAttribute('data-look-pending', Boolean(this.pending));
      this.toggleAttribute('data-look-uncertain', Boolean(this.uncertain));
      if (field) {
        // Disabled fields are omitted from FormData; inert prevents interaction without dropping id.
        field.disabled = Boolean(this.uncertain);
        field.inert = locked;
        if (locked) field.setAttribute('aria-disabled', 'true'); else field.removeAttribute('aria-disabled');
      }
      const id = this.choice();
      const soldOut = field?.tagName === 'SELECT'
        ? [...field.options].filter(option => option.value).every(option => option.disabled)
        : field?.dataset.available === 'false';
      const price = this.querySelector('[data-look-price]');
      const template = [...this.querySelectorAll('template[data-look-variant-price]')].find(item => item.dataset.lookVariantPrice === id);
      if (price && template) price.innerHTML = template.innerHTML;
      for (const wishlist of this.querySelectorAll('wishlist-button-block')) {
        if (id) wishlist.setAttribute('data-variant-id', id); else wishlist.removeAttribute('data-variant-id');
      }
      for (const button of this.form?.querySelectorAll('[type="submit"]') || []) {
        button.disabled = locked || !id || !interactive() || this.closest('raith-shop-look')?.allowsRow?.(this) === false;
        if (this.pending) continue; // Native product-form owns the busy indicator.
        button.removeAttribute('aria-busy');
        button.textContent = soldOut ? (this.dataset.soldOutLabel || 'Sold out') : (this.dataset.addLabel || 'Add to bag');
      }
    }
    beforeSubmit(event) {
      const owner = this.closest('raith-shop-look');
      const id = this.choice();
      if (event.target !== this.form || !owner?.canPresent() || !owner.allowsRow(this) || !id || this.pending || this.uncertain || !interactive()) {
        event.preventDefault(); event.stopImmediatePropagation(); return;
      }
      if (!this.form.checkValidity()) return;
      owner.markInteraction();
      this.pending = {id, closeRevision: owner.closeRevision};
      this.message('[data-look-status]', '');
      this.message('[data-look-error]', '');
      this.sync();
    }
    added(event) {
      if (event.target !== this.form || !this.pending || !receiptHas(event, this.pending.id)) return;
      const owner = this.closest('raith-shop-look');
      if (owner?.matchingSet() && !owner.acceptCart(event.detail?.cart, {productId: this.dataset.productId, variantId: this.pending.id})) {
        this.unconfirmed();
        return;
      }
      if (event.detail?.onSuccessDo === 'keep_look_open' && event.cancelable && owner?.canPresent()) {
        event.detail.lookController = owner;
        event.preventDefault();
        owner.confirmAdded(false, this.pending.closeRevision);
      }
      this.pending = null;
      if (this.field()?.tagName === 'SELECT') this.field().value = '';
      this.message('[data-look-status]', this.dataset.addedLabel || 'Added to bag');
      this.message('[data-look-error]', '');
      this.sync();
    }
  }

  class RaithShopLook extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.drawer = this.querySelector('[data-look-drawer]');
      this.entry = this.querySelector('[data-look-entry]') || this.entry;
      this.items = this.querySelector('[data-look-items]');
      this.closeRevision = 0;
      this.presentationRevision = 0;
      this.interacted = false;
      if (!this.drawer || !this.items) return;
      this.entry?.addEventListener('click', event => {
        event.preventDefault();
        // Native DialogElement also delegates aria-controls clicks from the body.
        event.stopPropagation();
        if (!interactive()) return;
        this.markInteraction();
        this.open().catch(() => {});
      }, {signal});
      this.addEventListener('click', event => {
        if (!event.target.closest('[data-look-view-bag]')) return;
        event.preventDefault();
        if (interactive()) this.viewBag().catch(() => this.cartPage());
      }, {signal});
      this.drawer.addEventListener('dialog:before-hide', () => {
        this.closeRevision += 1;
        this.presentationRevision += 1;
      }, {signal});
      for (const type of ['dialog:after-show', 'dialog:after-hide']) this.drawer.addEventListener(type, () => {
        this.entry?.setAttribute('aria-expanded', String(this.drawer.open));
      }, {signal});
      document.addEventListener('submit', event => this.beforeMainSubmit(event), {capture: true, signal});
      document.addEventListener('variant:add', event => this.mainAdded(event), {signal});
      for (const type of ['cart:error', 'cart:uncertain']) document.addEventListener(type, event => {
        if (event.target === this.mainAttempt?.form) this.restoreMainIntent();
      }, {signal});
      if (this.matchingSet()) {
        document.addEventListener('cart:change', event => this.acceptCart(event.detail?.cart), {signal});
        document.addEventListener('cart:refresh', () => this.refreshCart(), {signal});
        if (!this.cartSnapshot) {
          try { this.acceptCart(JSON.parse(this.querySelector('script[data-look-cart]')?.textContent || 'null')); }
          catch { this.invalidateCart(); }
        } else this.syncRemainder();
      }
      for (const type of ['shopify:inspector:activate', 'shopify:inspector:deactivate']) document.addEventListener(type, () => {
        this.querySelectorAll('raith-look-item').forEach(item => item.sync?.());
      }, {signal});
      document.addEventListener('shopify:section:load', () => this.refreshEntry(), {signal});
      document.addEventListener('shopify:section:select', event => this.editorSelect(event), {signal});
      document.addEventListener('shopify:block:select', event => this.editorSelect(event), {signal});
      document.addEventListener('shopify:section:deselect', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.drawer.hide?.();
      }, {signal});
      this.refreshEntry();
      customElements.whenDefined('vl-size-drawer').then(() => { if (!signal.aborted) this.refreshEntry(); });
      this.loadRecommendations(signal);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.cartRead?.abort();
      this.presentationRevision += 1;
      this.restoreMainIntent();
      this.entry?.remove();
      this.hero?.classList.remove('has-raith-look');
      const peers = [...this.ownerDocument.querySelectorAll('raith-shop-look')];
      queueMicrotask(() => peers.forEach(peer => { if (peer.isConnected) peer.refreshEntry?.(); }));
    }
    primary() {
      const products = [...document.querySelectorAll('raith-product.vl-product')];
      return products.find(product => product.querySelector('[data-offer-current]')?.dataset.productId === this.dataset.productId)
        || (products.length === 1 && !products[0].querySelector('[data-offer-current]') ? products[0] : null);
    }
    mainForm() {
      const product = this.primary();
      const id = this.dataset.mainFormId || product?.dataset.formId;
      const form = id ? document.getElementById(id) : null;
      return form?.tagName === 'FORM' && product?.contains(form) ? form : null;
    }
    rows() {
      const seen = new Set();
      const rows = [...(this.items?.querySelectorAll('raith-look-item') || [])].filter(item => {
        const id = item.dataset.productId;
        if (!variantId(id) || item.dataset.available !== 'true' || id === this.dataset.productId || seen.has(id)) return false;
        seen.add(id); return true;
      });
      return this.matchingSet() ? rows : rows.slice(0, 4);
    }
    matchingSet() { return this.dataset.lookMode === 'matching-set'; }
    contextCurrent() {
      if (!this.matchingSet()) return true;
      if (!/^[A-Z]{3}$/.test(this.dataset.currency || '') || !/^[A-Z]{2}$/.test(this.dataset.country || '') || !this.dataset.language) return false;
      const marker = this.primary()?.querySelector('[data-offer-current]');
      return !marker || ['currency', 'country', 'language'].every(key => !marker.dataset[key] || marker.dataset[key] === this.dataset[key]);
    }
    remainingRows() {
      if (!this.matchingSet()) return this.rows();
      if (!this.cartSnapshot || !this.contextCurrent()) return [];
      return this.rows().filter(row => !this.cartSnapshot.items.some(item => item.productId === row.dataset.productId));
    }
    allowsRow(row) { return !this.matchingSet() || this.remainingRows().includes(row); }
    invalidateCart() {
      this.cartSnapshot = null;
      this.syncRemainder();
    }
    acceptCart(cart, expected = null) {
      if (!this.matchingSet()) return true;
      if (!this.contextCurrent() || cart?.currency !== this.dataset.currency || !Array.isArray(cart.items)
        || cart.items.some(item => !variantId(item?.product_id) || !variantId(item?.variant_id ?? item?.id) || !Number.isSafeInteger(item?.quantity) || item.quantity <= 0)
        || (cart.item_count !== undefined && cart.item_count !== cart.items.reduce((sum, item) => sum + item.quantity, 0))) {
        this.invalidateCart(); return false;
      }
      const items = cart.items.map(item => ({productId: String(item.product_id), variantId: String(item.variant_id ?? item.id), quantity: item.quantity}));
      if (expected && !items.some(item => item.productId === String(expected.productId) && item.variantId === String(expected.variantId))) {
        this.invalidateCart(); return false;
      }
      this.cartSnapshot = {currency: cart.currency, items};
      this.cartRead?.abort();
      this.syncRemainder();
      return true;
    }
    syncRemainder() {
      if (!this.matchingSet()) return;
      const remaining = this.remainingRows();
      for (const row of this.items?.querySelectorAll('raith-look-item') || []) {
        // Keep the form and its unknown-write lock alive when an item is already in the bag.
        row.hidden = Boolean(this.cartSnapshot) && !remaining.includes(row) && !row.pending && !row.uncertain;
        row.sync?.();
      }
      this.refreshEntry();
    }
    async refreshCart() {
      if (!this.matchingSet()) return;
      this.cartRead?.abort();
      this.invalidateCart();
      if (!this.ownsPresentation() || !this.contextCurrent() || this.rows().length === 0) return;
      const request = this.cartRead = new AbortController();
      const timer = setTimeout(() => request.abort(), 8000);
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {credentials: 'same-origin', cache: 'no-store', signal: request.signal});
        if (!response.ok) return;
        const cart = await response.json();
        if (request.signal.aborted || this.cartRead !== request || !this.isConnected) return;
        this.acceptCart(cart);
      } catch { /* Unknown contents must not make a missing-piece purchase available. */ }
      finally { clearTimeout(timer); if (this.cartRead === request) this.cartRead = null; }
    }
    canPresent() {
      // The native add handoff also uses this capability while the final row closes into the bag.
      return this.ownsPresentation() && this.contextCurrent() && this.drawer?.isConnected && typeof this.drawer.show === 'function'
        && this.rows().length > 0;
    }
    ownsPresentation() {
      return this.isConnected && [...document.querySelectorAll('raith-shop-look')]
        .find(module => module.dataset.productId === this.dataset.productId) === this;
    }
    refreshEntry() {
      if (!this.ownsPresentation()) {
        if (this.entry) { this.entry.hidden = true; this.entry.disabled = true; }
        return;
      }
      const hero = this.primary()?.querySelector('.vl-product__hero');
      if (this.hero !== hero) this.hero?.classList.remove('has-raith-look');
      this.hero = hero;
      if (this.entry) {
        this.entry.hidden = !(hero && this.canPresent() && this.remainingRows().length);
        this.entry.disabled = this.entry.hidden;
        this.entry.setAttribute('aria-expanded', String(Boolean(this.drawer?.open)));
      }
      if (hero && this.entry) {
        if (this.entry.parentElement !== hero) hero.append(this.entry);
        hero.classList.toggle('has-raith-look', !this.entry.hidden);
      }
      if (this.canPresent()) this.drawer.hidden = false;
    }
    markInteraction() { this.interacted = true; }
    beforeMainSubmit(event) {
      const form = this.mainForm();
      if (event.target !== form) return;
      this.markInteraction();
      if (!interactive() || this.dataset.autoOpen !== 'true' || !this.canPresent()
        || window.themeVariables?.settings?.cartType === 'page' || !form.checkValidity()
        || event.submitter?.disabled || event.submitter?.getAttribute('aria-busy') === 'true') return;
      const id = form.elements.namedItem('id')?.value;
      if (!variantId(id)) return;
      this.restoreMainIntent();
      let input = form.querySelector('input[name="on_success"]');
      const created = !input;
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'on_success'; form.append(input); }
      this.mainAttempt = {form, input, created, previous: input.value, id, closeRevision: this.closeRevision};
      input.value = 'show_shop_look';
    }
    restoreMainIntent() {
      const attempt = this.mainAttempt;
      this.mainAttempt = null;
      if (!attempt || attempt.input.value !== 'show_shop_look') return;
      if (attempt.created) attempt.input.remove(); else attempt.input.value = attempt.previous;
    }
    mainAdded(event) {
      const attempt = this.mainAttempt;
      if (!attempt || event.target !== attempt.form) return;
      this.restoreMainIntent();
      if (event.detail?.onSuccessDo !== 'show_shop_look' || !event.cancelable || !receiptHas(event, attempt.id)
        || !this.canPresent() || attempt.closeRevision !== this.closeRevision) return;
      if (this.matchingSet() && (!this.acceptCart(event.detail?.cart, {productId: this.dataset.productId, variantId: attempt.id}) || !this.remainingRows().length)) return;
      event.detail.lookController = this;
      event.preventDefault();
      this.confirmAdded(true);
    }
    confirmAdded(open, closeRevision = this.closeRevision) {
      const confirmation = this.querySelector('[data-look-confirmation]');
      if (confirmation) confirmation.hidden = false;
      if (this.matchingSet() && !this.remainingRows().length) {
        if (this.drawer.open && closeRevision === this.closeRevision) this.viewBag().catch(() => this.cartPage());
      } else if (open) {
        const recover = () => {
          if (this.isConnected && closeRevision === this.closeRevision) this.viewBag().catch(() => this.cartPage());
        };
        this.open().then(opened => { if (!opened) recover(); }, recover);
      }
    }
    async open() {
      if (!this.canPresent() || this.matchingSet() && !this.remainingRows().length) return false;
      const revision = ++this.presentationRevision;
      const others = [this.primary()?.querySelector('.vl-size-drawer'), document.querySelector('cart-drawer')];
      for (const other of others) if (other?.open) await other.hide();
      if (!this.isConnected || revision !== this.presentationRevision || !this.canPresent() || this.matchingSet() && !this.remainingRows().length) return false;
      this.drawer.hidden = false;
      await this.drawer.show();
      return this.drawer.open;
    }
    async viewBag() {
      if (this.viewingBag) return;
      this.viewingBag = true;
      this.markInteraction();
      const buttons = [...this.querySelectorAll('button[data-look-view-bag]')];
      buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      try {
        await this.drawer.hide();
        if (!this.isConnected) return;
        const cart = document.querySelector('cart-drawer');
        const currency = cart?.dataset.cartCurrency;
        if (typeof cart?.presentCart === 'function' && /^[A-Z]{3}$/.test(currency || '')) {
          // A concurrent native receipt can supersede section rendering during the handoff.
          // Confirm once more with GET only; never repeat the add.
          const attempts = this.matchingSet() ? 2 : 1;
          for (let attempt = 0; attempt < attempts; attempt++) {
            if (!this.isConnected || cart.dataset.cartCurrency !== currency) break;
            if (await cart.presentCart({currency}, {open: true})) return;
          }
        }
        this.cartPage();
      } finally {
        this.viewingBag = false;
        buttons.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
      }
    }
    cartPage() {
      if (this.isConnected) window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
    }
    editorSelect(event) {
      if (!window.Shopify?.designMode) return;
      const sectionMatches = event.detail?.sectionId === this.dataset.sectionId
        || event.target === this.closest('.shopify-section');
      if (!sectionMatches) return;
      if (event.type === 'shopify:block:select' && event.detail?.blockId !== this.dataset.lookBlockId) return;
      this.open().catch(() => {});
    }
    async loadRecommendations(signal) {
      if (this.matchingSet()) return;
      if (!this.dataset.recommendationsUrl) return;
      try {
        const url = new URL(this.dataset.recommendationsUrl, window.location.href);
        const route = window.Shopify?.routes?.root || '/';
        if (url.origin !== window.location.origin || url.pathname !== `${route}recommendations/products`) return;
        const response = await fetch(url.href, {signal, headers: {'X-Requested-With': 'XMLHttpRequest'}});
        if (!response.ok || signal.aborted) return;
        const html = await response.text();
        if (signal.aborted || this.interacted || !this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = [...parsed.querySelectorAll('raith-shop-look')].find(element => element.dataset.productId === this.dataset.productId
          && element.dataset.sectionId === this.dataset.sectionId);
        const sourceItems = source?.querySelector('[data-look-items]');
        if (!sourceItems) return;
        const seen = new Set([this.dataset.productId]);
        const rows = [...sourceItems.querySelectorAll('raith-look-item')].filter(row => {
          const id = row.dataset.productId;
          if (!variantId(id) || row.dataset.available !== 'true' || seen.has(id)) return false;
          seen.add(id); return true;
        }).slice(0, 4);
        if (!rows.length) return;
        this.items.replaceChildren(...rows.map(row => document.importNode(row, true)));
        this.refreshEntry();
      } catch { /* Keep the merchant's saved look, or the normal native cart when empty. */ }
    }
  }
  if (!customElements.get('raith-look-item')) customElements.define('raith-look-item', RaithLookItem);
  if (!customElements.get('raith-shop-look')) customElements.define('raith-shop-look', RaithShopLook);
})();
